import useGetBranch from "@/shared/hooks/useGetBranch";
import useGetEmployeeSchedule from "@/shared/hooks/useGetEmployeeSchedule";
import { Button, Card, Flex, Radio, Typography, type RadioChangeEvent } from "antd";
import dayjs from "dayjs";
import { useFormContext, useWatch } from "react-hook-form";
import { type FormValues } from "../../model/form.schema";
import useEmployeeScreenStyles from "./EmployeeScreen.styles";
import { useSteps } from "@/shared/hooks/useGetSteps";

const EmployeeScreen = () => {
  // ! ||--------------------------------------------------------------------------------
  // ! ||                                      hooks
  // ! ||--------------------------------------------------------------------------------

  const { styles, cx } = useEmployeeScreenStyles();
  const { control, setValue, unregister } = useFormContext<FormValues>();
  const { branchData } = useGetBranch();
  const { onNextStep, steps } = useSteps();

  const date = useWatch({ control, name: "date" });
  const end_time = useWatch({ control, name: "end_time" });
  const start_time = useWatch({ control, name: "start_time" });
  const duration = useWatch({ control, name: "duration" });
  const employee_id = useWatch({ control, name: "employee_id" });

  const { employeeScheduleData, loading } = useGetEmployeeSchedule({
    date: date || dayjs(),
    duration: duration || branchData?.branch.online_booking_durations[0] || 30,
  });

  // ! ||--------------------------------------------------------------------------------
  // ! ||                                      Logic
  // ! ||--------------------------------------------------------------------------------
  const scheduleFirst = steps[0].type === "dateTime";
  const onChange = (e: RadioChangeEvent) => {
    setValue("employee_id", e.target.value);
    if (!scheduleFirst) {
      unregister("start_time");
      unregister("end_time");
    }
  };

  const scheduleFirstEmployees =
    employeeScheduleData?.employees.filter(
      (emp) => emp.slots.find((slot) => slot.end === end_time && slot.start === start_time) && emp.slots.length,
    ) || [];
  const employeeFirstEmployees = employeeScheduleData?.employees.filter((emp) => emp.slots.length) || [];

  const handleSlotPick = (start: string, end: string, empId: number) => {
    setValue("start_time", start);
    setValue("end_time", end);
    setValue("employee_id", empId);
  };

  const currEmployee = scheduleFirst
    ? scheduleFirstEmployees.find((emp) => emp.employee.id === employee_id)
    : employeeScheduleData?.employees.find((emp) => emp.employee.id === employee_id);

  // ! ||--------------------------------------------------------------------------------
  // ! ||                                      render
  // ! ||--------------------------------------------------------------------------------

  return (
    <Card className={styles.screen} loading={loading}>
      {!scheduleFirstEmployees.length && !employeeFirstEmployees.length ? (
        <Flex className="emptySlots" vertical align="center">
          <Typography.Paragraph className="noTime">В этом филиале нет доступных специалистов</Typography.Paragraph>
        </Flex>
      ) : (
        <Radio.Group
          onChange={onChange}
          className={styles.radio}
          value={employee_id}
          options={
            scheduleFirst
              ? scheduleFirstEmployees.map((emp) => ({
                  value: emp.employee.id,

                  label: (
                    <Flex className="labelWrapper" gap={8}>
                      {emp.employee.avatar?.url ? (
                        <img className="avatar" src={emp.employee.avatar?.url} />
                      ) : (
                        <div className="avatarPlug">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M12 13C14.7614 13 17 10.7614 17 8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8C7 10.7614 9.23858 13 12 13Z"
                              stroke="#8C8C8C"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M20 21C20 18.8783 19.1571 16.8434 17.6569 15.3431C16.1566 13.8429 14.1217 13 12 13C9.87827 13 7.84344 13.8429 6.34315 15.3431C4.84285 16.8434 4 18.8783 4 21"
                              stroke="#8C8C8C"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </div>
                      )}

                      <Flex vertical justify="space-between" className="label" gap={8}>
                        <Typography.Text className="name">
                          {cx(emp.employee.first_name, emp.employee.middle_name, emp.employee.last_name)}
                        </Typography.Text>
                        <Typography.Text className="specialty">
                          {emp.employee.specialties
                            .map((sp) => sp.name)
                            .filter((i) => i.trim())
                            .join(", ")}
                        </Typography.Text>
                      </Flex>
                    </Flex>
                  ),
                }))
              : employeeFirstEmployees.map((emp) => ({
                  value: emp.employee.id,

                  label: (
                    <Flex className="labelWrapper" gap={8}>
                      {emp.employee.avatar?.url ? (
                        <img className="avatar" src={emp.employee.avatar?.url} />
                      ) : (
                        <div className="avatarPlug">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M12 13C14.7614 13 17 10.7614 17 8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8C7 10.7614 9.23858 13 12 13Z"
                              stroke="#8C8C8C"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M20 21C20 18.8783 19.1571 16.8434 17.6569 15.3431C16.1566 13.8429 14.1217 13 12 13C9.87827 13 7.84344 13.8429 6.34315 15.3431C4.84285 16.8434 4 18.8783 4 21"
                              stroke="#8C8C8C"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </div>
                      )}

                      <Flex vertical justify="space-between" className="label" gap={8}>
                        <Typography.Text className="name">
                          {cx(emp.employee.first_name, emp.employee.middle_name, emp.employee.last_name)}
                        </Typography.Text>
                        <Typography.Text className="specialty">
                          {emp.employee.specialties
                            .map((sp) => sp.name)
                            .filter((i) => i.trim())
                            .join(", ")}
                        </Typography.Text>
                        <Typography.Text className="specialty">Сегодня:</Typography.Text>
                        <Flex gap={4} className="slots">
                          {emp.slots.map((slot) => (
                            <Flex
                              onClick={() => handleSlotPick(slot.start, slot.end, emp.employee.id)}
                              className={cx(
                                "slotItem",
                                start_time === slot.start &&
                                  end_time === slot.end &&
                                  employee_id === emp.employee.id &&
                                  "picked",
                              )}
                              key={`${slot.start} ${slot.end}`}
                            >
                              {slot.start}
                            </Flex>
                          ))}
                        </Flex>
                      </Flex>
                    </Flex>
                  ),
                }))
          }
        />
      )}

      <Flex className={cx("full-width", styles.footer)}>
        <Button
          onClick={() =>
            onNextStep(
              cx(
                currEmployee?.employee.first_name,
                currEmployee?.employee.middle_name,
                currEmployee?.employee.last_name,
              ),
            )
          }
          type="primary"
          className="full-width"
          disabled={!employee_id}
        >
          Далее
        </Button>
      </Flex>
    </Card>
  );
};
export default EmployeeScreen;
